// src/pages/ExportDossiers.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import MuseumExport from "../components/MuseumExport";
import MuseumLoanExport from "../components/MuseumLoanExport";
import CollectionBookExport from "../components/CollectionBookExport";

const safeStr = (v) => (typeof v === "string" ? v.trim() : "");
const isAuthError = (err) => {
  const msg = (err?.message || "").toLowerCase();
  const code = err?.code || err?.status;
  return (
    code === 401 ||
    code === 403 ||
    msg.includes("jwt") ||
    msg.includes("not authorized") ||
    msg.includes("permission denied") ||
    msg.includes("invalid token")
  );
};

export const ExportDossiers = () => {
  const navigate = useNavigate();
  const { user: ctxUser, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  // On garde un user local “résilient” (utile si ctxUser flicker/null à cause d’un refresh token)
  const [user, setUser] = useState(ctxUser || null);

  const [artworks, setArtworks] = useState([]);
  const [selectedArtworks, setSelectedArtworks] = useState([]);
  const [exportFormat, setExportFormat] = useState("book"); // 'donation' | 'loan' | 'book'
  const [isLoading, setIsLoading] = useState(true);
  const [collectionData, setCollectionData] = useState(null);
  const [userData, setUserData] = useState(null);

  // Sync user depuis le context
  useEffect(() => {
    setUser(ctxUser || null);
  }, [ctxUser]);

  // Fallback: si le context met user=null momentanément, on tente de récupérer le user Supabase
  useEffect(() => {
    let cancelled = false;

    const ensureUser = async () => {
      if (authLoading) return;
      if (ctxUser) return;
      const { data, error } = await supabase.auth.getUser();
      if (!cancelled && !error && data?.user) setUser(data.user);
    };

    ensureUser();

    return () => {
      cancelled = true;
    };
  }, [authLoading, ctxUser]);

  // Charger les données (sans select("*") pour éviter les colonnes “non lisibles” selon RLS/roles)
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!user?.id) return;

      setIsLoading(true);

      try {
        // 1) Œuvres
        const { data: artworksData, error: artworksError } = await supabase
          .from("artworks")
          .select("id, title, artist, year, medium, image_url, created_at, client_id")
          .eq("client_id", user.id)
          .order("created_at", { ascending: false });

        if (artworksError) throw artworksError;

        if (!cancelled) {
          setArtworks(artworksData || []);
        }

        // 2) Profil utilisateur (safe fields)
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("id, full_name, company_name, collecting_since")
          .eq("id", user.id)
          .single();

        // Si profil non lisible selon RLS, on ne casse pas tout
        if (!profileError && profile && !cancelled) {
          const fullName = safeStr(profile.full_name);
          const company = safeStr(profile.company_name);

          setUserData({
            name: fullName || company || t("exportDossiers.defaults.collector"),
            collecting_since: safeStr(profile.collecting_since) || "2010",
          });

          setCollectionData({
            name: fullName || company || t("exportDossiers.defaults.collectionName"),
            type: t("exportDossiers.defaults.collectionType"),
            focus: t("exportDossiers.defaults.collectionFocus"),
            history: t("exportDossiers.defaults.collectionHistory"),
            main_themes: [
              t("exportDossiers.defaults.themeContemporary"),
              t("exportDossiers.defaults.themePainting"),
              t("exportDossiers.defaults.themeSculpture"),
            ],
          });
        } else if (!cancelled) {
          // fallback si profil inaccessible
          setUserData({ name: t("exportDossiers.defaults.collector"), collecting_since: "2010" });
          setCollectionData({
            name: t("exportDossiers.defaults.collectionName"),
            type: t("exportDossiers.defaults.collectionType"),
            focus: t("exportDossiers.defaults.collectionFocus"),
            history: t("exportDossiers.defaults.collectionHistory"),
            main_themes: [
              t("exportDossiers.defaults.themeContemporary"),
              t("exportDossiers.defaults.themePainting"),
              t("exportDossiers.defaults.themeSculpture"),
            ],
          });
        }
      } catch (err) {
        console.error("ExportDossiers: erreur chargement données:", err);

        // Si c’est vraiment un souci auth, on renvoie vers login (au lieu de cascader vers des guards bizarres)
        if (isAuthError(err)) {
          if (!cancelled) {
            setArtworks([]);
            setSelectedArtworks([]);
          }
          navigate("/login");
          return;
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [user?.id, navigate, t]);

  const toggleSelection = (artwork) => {
    setSelectedArtworks((prev) => {
      if (prev.find((a) => a.id === artwork.id)) return prev.filter((a) => a.id !== artwork.id);
      return [...prev, artwork];
    });
  };

  const selectAll = () => {
    setSelectedArtworks((prev) => {
      if (prev.length === artworks.length) return [];
      return [...artworks];
    });
  };

  // Loading auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black mx-auto mb-4" />
          <p className="text-gray-600">{t("exportDossiers.loading")}</p>
        </div>
      </div>
    );
  }

  // Non connecté
  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-light mb-4">{t("exportDossiers.authRequired.title")}</h2>
          <p className="text-gray-600 mb-6">{t("exportDossiers.authRequired.text")}</p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            {t("exportDossiers.authRequired.login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div
        style={{
          padding: "30px 40px",
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #eee",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h1
            style={{
              fontSize: "32px",
              fontFamily: "Georgia, serif",
              fontWeight: "400",
              margin: "0 0 8px 0",
              color: "#000",
            }}
          >
            {t("exportDossiers.title")}
          </h1>
          <p style={{ fontSize: "14px", color: "#666", margin: "0 0 30px 0" }}>
            {t("exportDossiers.subtitle")}
          </p>

          {/* Sélecteur de format */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "15px",
              marginBottom: "20px",
            }}
          >
            <FormatButton
              active={exportFormat === "donation"}
              onClick={() => setExportFormat("donation")}
              icon="📄"
              title={t("exportDossiers.formats.donation.title")}
              subtitle={t("exportDossiers.formats.donation.pages")}
              description={t("exportDossiers.formats.donation.description")}
            />

            <FormatButton
              active={exportFormat === "loan"}
              onClick={() => setExportFormat("loan")}
              icon="🤝"
              title={t("exportDossiers.formats.loan.title")}
              subtitle={t("exportDossiers.formats.loan.pages")}
              description={t("exportDossiers.formats.loan.description")}
            />

            <FormatButton
              active={exportFormat === "book"}
              onClick={() => setExportFormat("book")}
              icon="📚"
              title={t("exportDossiers.formats.book.title")}
              subtitle={t("exportDossiers.formats.book.pages")}
              description={t("exportDossiers.formats.book.description")}
            />
          </div>

          {/* Description format */}
          <FormatDescription format={exportFormat} t={t} />

          {/* Actions */}
          <div style={{ display: "flex", gap: "15px", alignItems: "center", marginTop: "20px" }}>
            <button
              onClick={selectAll}
              style={{
                padding: "10px 20px",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ddd",
                borderRadius: "2px",
                fontSize: "14px",
                fontFamily: "Georgia, serif",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {selectedArtworks.length === artworks.length
                ? t("exportDossiers.actions.deselectAll")
                : t("exportDossiers.actions.selectAll")}
            </button>

            {exportFormat === "donation" && (
              <MuseumExport selectedArtworks={selectedArtworks} collectionData={collectionData} userData={userData} />
            )}

            {exportFormat === "loan" && (
              <MuseumLoanExport
                selectedArtworks={selectedArtworks}
                collectionData={collectionData}
                userData={userData}
              />
            )}

            {exportFormat === "book" && (
              <CollectionBookExport
                selectedArtworks={selectedArtworks}
                collectionData={collectionData}
                userData={userData}
              />
            )}

            <span style={{ fontSize: "13px", color: "#666", marginLeft: "auto" }}>
              {t("exportDossiers.selection.count", {
                selected: selectedArtworks.length,
                total: artworks.length,
                count: artworks.length,
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Grille des œuvres */}
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "40px" }}>
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black mx-auto mb-4" />
            <p className="text-gray-600">{t("exportDossiers.artworks.loading")}</p>
          </div>
        ) : artworks.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", backgroundColor: "#fafafa", borderRadius: "2px" }}>
            <p style={{ fontSize: "16px", color: "#666", marginBottom: "20px" }}>{t("exportDossiers.artworks.empty")}</p>
            <button
              onClick={() => navigate("/collection")}
              style={{
                padding: "12px 24px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "2px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              {t("exportDossiers.artworks.add")}
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "25px",
            }}
          >
            {artworks.map((artwork) => {
              const isSelected = !!selectedArtworks.find((a) => a.id === artwork.id);

              return (
                <div
                  key={artwork.id}
                  onClick={() => toggleSelection(artwork)}
                  style={{
                    position: "relative",
                    backgroundColor: "#fff",
                    borderRadius: "2px",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: isSelected ? "3px solid #000" : "1px solid #eee",
                    transition: "all 0.2s ease",
                    boxShadow: isSelected ? "0 4px 12px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {/* Checkbox */}
                  <div
                    style={{
                      position: "absolute",
                      top: "15px",
                      left: "15px",
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: isSelected ? "#000" : "rgba(255,255,255,0.9)",
                      border: `2px solid ${isSelected ? "#000" : "#999"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      backdropFilter: "blur(5px)",
                    }}
                  >
                    {isSelected && <span style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}>✓</span>}
                  </div>

                  {/* Image */}
                  <div style={{ width: "100%", height: "280px", backgroundColor: "#f5f5f5", overflow: "hidden" }}>
                    {artwork.image_url ? (
                      <img
                        src={artwork.image_url}
                        alt={artwork.title || t("exportDossiers.artworks.fallbackArtwork")}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#999",
                        }}
                      >
                        {t("exportDossiers.artworks.noImage")}
                      </div>
                    )}
                  </div>

                  {/* Infos */}
                  <div style={{ padding: "20px" }}>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontFamily: "Georgia, serif",
                        fontWeight: "600",
                        margin: "0 0 5px 0",
                        color: "#000",
                      }}
                    >
                      {artwork.artist || t("exportDossiers.artworks.unknownArtist")}
                    </h3>
                    <p
                      style={{
                        fontSize: "14px",
                        fontFamily: "Georgia, serif",
                        fontStyle: "italic",
                        margin: "0 0 10px 0",
                        color: "#666",
                      }}
                    >
                      {artwork.title || t("exportDossiers.artworks.untitled")}
                    </p>
                    <div style={{ fontSize: "12px", color: "#999" }}>
                      {artwork.year ? `${artwork.year} • ` : ""}
                      {artwork.medium || t("exportDossiers.artworks.mediumUnknown")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Composant bouton format
const FormatButton = ({ active, onClick, icon, title, subtitle, description }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      padding: "20px",
      backgroundColor: active ? "#000" : "#f5f5f5",
      color: active ? "#fff" : "#000",
      border: active ? "2px solid #000" : "1px solid #ddd",
      borderRadius: "2px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      textAlign: "left",
    }}
  >
    <div style={{ fontSize: "24px", marginBottom: "8px" }}>{icon}</div>
    <div style={{ fontSize: "16px", fontFamily: "Georgia, serif", fontWeight: "600", marginBottom: "4px" }}>
      {title}
    </div>
    <div style={{ fontSize: "12px", opacity: active ? 0.8 : 0.6, marginBottom: "4px" }}>{subtitle}</div>
    <div style={{ fontSize: "13px", opacity: active ? 0.9 : 0.7 }}>{description}</div>
  </button>
);

// Composant description format
const FormatDescription = ({ format, t }) => {
  const descriptions = {
    donation: {
      title: t("exportDossiers.descriptions.donation.title"),
      text: t("exportDossiers.descriptions.donation.text"),
      color: "#e3f2fd",
      features: [
        t("exportDossiers.descriptions.donation.features.history"),
        t("exportDossiers.descriptions.donation.features.tax"),
        t("exportDossiers.descriptions.donation.features.schedule"),
        t("exportDossiers.descriptions.donation.features.appendices"),
      ],
    },
    loan: {
      title: t("exportDossiers.descriptions.loan.title"),
      text: t("exportDossiers.descriptions.loan.text"),
      color: "#f3e5f5",
      features: [
        t("exportDossiers.descriptions.loan.features.insurance"),
        t("exportDossiers.descriptions.loan.features.transport"),
        t("exportDossiers.descriptions.loan.features.conservation"),
        t("exportDossiers.descriptions.loan.features.facility"),
      ],
    },
    book: {
      title: t("exportDossiers.descriptions.book.title"),
      text: t("exportDossiers.descriptions.book.text"),
      color: "#fff3e0",
      features: [
        t("exportDossiers.descriptions.book.features.essay"),
        t("exportDossiers.descriptions.book.features.organisation"),
        t("exportDossiers.descriptions.book.features.index"),
        t("exportDossiers.descriptions.book.features.colophon"),
      ],
    },
  };

  const desc = descriptions[format];

  return (
    <div style={{ padding: "20px", backgroundColor: desc.color, borderRadius: "2px", borderLeft: "3px solid #000" }}>
      <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px" }}>{desc.title}</div>
      <p style={{ fontSize: "13px", color: "#333", margin: "0 0 12px 0", lineHeight: "1.6" }}>{desc.text}</p>
      <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
        {desc.features.map((feature, idx) => (
          <span
            key={idx}
            style={{
              fontSize: "11px",
              padding: "4px 10px",
              backgroundColor: "rgba(0,0,0,0.05)",
              borderRadius: "12px",
              color: "#666",
            }}
          >
            ✓ {feature}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ExportDossiers;
